import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { TabsContent } from '@/components/ui/tabs';
import { formatCurrency } from '@/lib/utils/formatters';
import { lerNumeroDigitado } from '@/lib/utils/numero-digitado';
import { motion } from 'framer-motion';
import { useValidarCalculadoraRTC } from '@/services/ApuracaoCBS';

const ROTULOS = {
  ValorBem: 'Valor do Bem',
  Icms: 'ICMS',
  Iss: 'ISS',
  Pis: 'PIS',
  Cofins: 'COFINS',
} as const;

type CampoDigitado = keyof typeof ROTULOS;

function todosLidos(
  valores: Record<CampoDigitado, number | null>
): valores is Record<CampoDigitado, number> {
  return Object.values(valores).every(valor => valor !== null);
}

export function CalculadoraRtcTab() {
  const [valorBem, setValorBem] = useState('');
  const [icms, setIcms] = useState('');
  const [iss, setIss] = useState('');
  const [pis, setPis] = useState('');
  const [cofins, setCofins] = useState('');
  const [camposInvalidos, setCamposInvalidos] = useState<CampoDigitado[]>([]);

  const validarMutation = useValidarCalculadoraRTC();

  const handleValidar = () => {
    const valores = {
      ValorBem: lerNumeroDigitado(valorBem),
      Icms: lerNumeroDigitado(icms),
      Iss: lerNumeroDigitado(iss),
      Pis: lerNumeroDigitado(pis),
      Cofins: lerNumeroDigitado(cofins),
    };

    if (!todosLidos(valores)) {
      // A base da conferência anterior não pode ficar ao lado do aviso, como se fosse destes valores.
      validarMutation.reset();
      setCamposInvalidos(
        (Object.keys(valores) as CampoDigitado[]).filter(campo => valores[campo] === null)
      );
      return;
    }

    setCamposInvalidos([]);
    validarMutation.mutate({
      AnoFatoGerador: new Date().getFullYear(),
      AjusteAcrescimos: 0,
      Descontos: 0,
      ...valores,
    });
  };

  const baseOficial = validarMutation.data?.data?.BaseCalculoOficial;

  return (
    <TabsContent value="calculadora-rtc" className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
        className="bg-primary text-primary-foreground px-6 py-3 rounded-lg"
      >
        <h2 className="text-xl font-bold">Calculadora RTC</h2>
      </motion.div>

      <Card>
        <CardHeader className="pb-3">
          <p className="text-sm font-semibold">Validar Base de Cálculo com a Calculadora RTC</p>
          <p className="text-xs text-muted-foreground">
            API pública da Receita (sem credencial) — confere a base de cálculo de CBS/IBS de uma
            operação contra o motor oficial.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-5">
            <Input
              placeholder="Valor do Bem"
              inputMode="decimal"
              value={valorBem}
              aria-invalid={camposInvalidos.includes('ValorBem')}
              onChange={e => setValorBem(e.target.value)}
            />
            <Input
              placeholder="ICMS"
              inputMode="decimal"
              value={icms}
              aria-invalid={camposInvalidos.includes('Icms')}
              onChange={e => setIcms(e.target.value)}
            />
            <Input
              placeholder="ISS"
              inputMode="decimal"
              value={iss}
              aria-invalid={camposInvalidos.includes('Iss')}
              onChange={e => setIss(e.target.value)}
            />
            <Input
              placeholder="PIS"
              inputMode="decimal"
              value={pis}
              aria-invalid={camposInvalidos.includes('Pis')}
              onChange={e => setPis(e.target.value)}
            />
            <Input
              placeholder="COFINS"
              inputMode="decimal"
              value={cofins}
              aria-invalid={camposInvalidos.includes('Cofins')}
              onChange={e => setCofins(e.target.value)}
            />
          </div>
          {camposInvalidos.length > 0 && (
            <p role="alert" className="text-sm text-destructive">
              Valor não reconhecido em: {camposInvalidos.map(campo => ROTULOS[campo]).join(', ')}.
              Digite só números, com vírgula nos centavos (por exemplo, 1.234,56).
            </p>
          )}
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              disabled={validarMutation.isPending}
              onClick={handleValidar}
            >
              Validar
            </Button>
            {baseOficial !== undefined && (
              <span className="text-sm">
                Base de cálculo oficial:{' '}
                <span className="font-semibold text-primary">{formatCurrency(baseOficial)}</span>
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </TabsContent>
  );
}
